import { useEffect, useMemo, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import clsx from "clsx"
import GameScreenLayout from "@/components/GameScreenLayout"
import { DeckBattleStartedPayload } from "@/types/battle"
import { useDeckDuelMachine } from "@/features/deck/duel/useDeckDuelMachine"
import { mapDeckRoundToRevealSides } from "@/features/deck/duel/mapDeckRoundResult"
import AbilityPicker from "@/features/duel/components/AbilityPicker"
import CardPicker from "@/features/duel/components/CardPicker"
import RevealSequence from "@/features/duel/components/RevealSequence"
import { useGuardiansPassport } from "@/features/passport/GuardiansPassportContext"
import { displayCardBS, formatPointsDelta } from "@/constants/game.constants"

export default function DeckDuelPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const initial = (location.state as { battle?: DeckBattleStartedPayload } | null)?.battle
  const { refreshPassport, bumpCards, passport } = useGuardiansPassport()
  const { state, availableCards, playCard, roundRevealDone, reset } =
    useDeckDuelMachine(initial)

  const [selectedAbilityId, setSelectedAbilityId] = useState<string | null>(null)
  const matchAbilityRef = useRef<string | null | undefined>(undefined)

  const battle = state.battle
  const guardianRank =
    passport?.guardianRankInfo?.rank ?? passport?.guardianRank ?? 1

  useEffect(() => {
    matchAbilityRef.current = undefined
    setSelectedAbilityId(null)
  }, [battle?.battleId])

  useEffect(() => {
    if (state.uiPhase === "match_end" && state.matchResult) {
      void refreshPassport()
      bumpCards()
    }
  }, [state.uiPhase, state.matchResult, refreshPassport, bumpCards])

  const abilityLocked = matchAbilityRef.current !== undefined
  const lockedAbilityId =
    matchAbilityRef.current === undefined ? selectedAbilityId : matchAbilityRef.current

  const roundReveal = useMemo(() => {
    if (!state.lastRoundResult || !battle) return null
    return mapDeckRoundToRevealSides(
      state.lastRoundResult,
      battle.you,
      battle.opponent,
      battle.you.userId,
      state.firstPlayerUserId,
      battle.yourDeck,
      state.yourPlayedCard?.id
    )
  }, [
    state.lastRoundResult,
    state.firstPlayerUserId,
    state.yourPlayedCard?.id,
    battle,
  ])

  if (state.uiPhase === "round_reveal" && roundReveal) {
    return (
      <GameScreenLayout title="Колодная дуэль">
        <RevealSequence
          yourSide={roundReveal.yourSide}
          opponentSide={roundReveal.opponentSide}
          onComplete={roundRevealDone}
        />
      </GameScreenLayout>
    )
  }

  if (state.uiPhase === "match_end" && state.matchResult) {
    const m = state.matchResult
    return (
      <GameScreenLayout title="Колодная дуэль">
        <div
          className={clsx(
            "rounded-xl border p-4 text-center text-sm",
            m.outcome === "WIN" ? "border-green-500/40" : "border-white/20"
          )}
        >
          <p className="text-xl font-bold text-guardians-accent">
            {m.outcome === "WIN"
              ? "Победа!"
              : m.outcome === "LOSE"
                ? "Поражение"
                : "Ничья"}
          </p>
          <p className="mt-2">{formatPointsDelta(m.ptsDelta)}</p>
          <button
            type="button"
            onClick={() => {
              reset()
              navigate("/play/deck-builder")
            }}
            className="mt-4 w-full rounded-xl bg-guardians-accent py-2 text-guardians-bg"
          >
            В лобби
          </button>
        </div>
      </GameScreenLayout>
    )
  }

  if (!battle) {
    return (
      <GameScreenLayout title="Колодная дуэль">
        <p className="text-sm text-guardians-muted">
          Нет боя.{" "}
          <button
            type="button"
            className="text-guardians-accent underline"
            onClick={() => navigate("/play/deck-builder")}
          >
            Собрать колоду
          </button>
        </p>
      </GameScreenLayout>
    )
  }

  const picking =
    state.uiPhase === "play_cards" &&
    !state.yourPlayedCard &&
    !state.socketReconnecting

  return (
    <GameScreenLayout title="Колодная дуэль">
      <p className="mb-3 text-sm text-guardians-muted">
        vs {battle.opponent.nickname} · раунд {battle.currentRound ?? 1} · счёт{" "}
        {battle.yourRoundWins ?? 0}:{battle.opponentRoundWins ?? 0}
        {state.timerSec != null ? ` · ⏱ ${state.timerSec}s` : ""}
      </p>

      {state.uiPhase === "play_cards_waiting" || state.yourPlayedCard ? (
        <p className="mb-3 text-sm text-guardians-accent">Ждём соперника…</p>
      ) : null}

      {state.socketReconnecting ? (
        <p className="mb-3 text-sm text-amber-200">Восстановление боя…</p>
      ) : null}

      {picking ? (
        <>
          <AbilityPicker
            affiliationElement={passport?.affiliationElement}
            guardianRank={guardianRank}
            selectedAbilityId={lockedAbilityId}
            onSelect={setSelectedAbilityId}
            locked={abilityLocked}
          />
          <CardPicker
            cards={availableCards}
            disabled={state.socketReconnecting}
            onSelect={(card) => {
              const abilityId = abilityLocked
                ? matchAbilityRef.current ?? null
                : selectedAbilityId
              if (!abilityLocked) matchAbilityRef.current = selectedAbilityId
              playCard(card.id, abilityId)
            }}
          />
        </>
      ) : (
        <ul className="grid grid-cols-2 gap-2 text-xs text-guardians-muted">
          {battle.yourDeck.map((card) => (
            <li key={card.id} className="rounded-lg border border-white/10 p-2">
              BS {displayCardBS(card)}
              {state.usedCardIds.includes(card.id) ? " · сыграна" : ""}
            </li>
          ))}
        </ul>
      )}

      {state.cancelled?.message ? (
        <p className="mt-3 text-sm text-red-300">{state.cancelled.message}</p>
      ) : null}
    </GameScreenLayout>
  )
}
